<script lang="ts" setup name="AdminScheduleRecipientListNew">
import { computed, reactive, ref, watch } from 'vue';
import { useVModel } from '@vueuse/core';

import { useSnackbarStore } from '~/store';

import type { IFormField, IOption } from '~/types';

import BaseButton from './BaseButton.vue';
import DataTable from './DataTable.vue';
import Field from './Field.vue';
import SearchField from './SearchField.vue';

const props = defineProps<{
  modelValue: string[];
  checkboxSelection?: boolean;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: IOption[]];
}>();

const snackbarStore = useSnackbarStore();

const inputValue = useVModel(props, 'modelValue', emit);

const tableSettings = {
  checkboxSelection: props.checkboxSelection,
  editable: false,
  columns: [{ header: 'Email', key: 'email', visible: true }],
};

interface IEmailField extends IFormField {
  rows?: number;
}

const schema = reactive<Record<string, IEmailField>>({
  email: {
    name: 'email',
    placeholder: 'Add emails, comma, semicolon separated ',
    component: 'FieldTextarea',
    rows: 1,
    label: 'email',
    trim: true,
    floatingLabel: false,
    errorList: [],
  },
});

const search = ref('');
const email = ref('');
const selectedTableRows = ref<{ id: string; email: string }[]>([]);

const tableRows = ref<{ id: string; email: string }[]>([]);
const visibleTableRows = computed(() => {
  if (!search.value) {
    return tableRows.value;
  }

  return tableRows.value.filter(({ email }) =>
    email.toLowerCase().includes(search.value.toLowerCase()),
  );
});

const addEmail = () => {
  const validEmails: string[] = [];
  const invalidEmails: string[] = [];
  const alreadyExistEmails: string[] = [];

  const emails = email.value
    .split(/[\s,;]+/)
    .map((e) => e.trim())
    .filter(Boolean);

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  for (const email of emails) {
    const isUniqueEmail =
      !inputValue.value.includes(email) && !validEmails.includes(email);

    const isValidEmail = emailRegex.test(email);

    if (!isValidEmail) {
      invalidEmails.push(email);
      continue;
    }

    if (!isUniqueEmail) {
      alreadyExistEmails.push(email);
      continue;
    }

    validEmails.push(email);
  }

  if (validEmails.length) {
    inputValue.value = [...validEmails, ...inputValue.value].sort((a, b) =>
      a.localeCompare(b),
    );

    snackbarStore.add({
      type: 'success',
      text: 'The recipient (-s) added.',
    });
  }

  if (invalidEmails.length) {
    email.value = [...invalidEmails, ...alreadyExistEmails].join(', ');
    schema.email.errorList = ['Invalid email address (-es). Please update.'];
    return;
  }

  if (alreadyExistEmails.length) {
    email.value = alreadyExistEmails.join(', ');
    schema.email.errorList = [
      'Email (-s) already exists in this list. You can’t add it twice.',
    ];
    return;
  }

  email.value = '';
  schema.email.errorList = [];
};

const removeEmail = () => {
  inputValue.value = inputValue.value.filter(
    (email) => !selectedTableRows.value.some((row) => row.email === email),
  );

  selectedTableRows.value = [];

  snackbarStore.add({
    type: 'success',
    text: 'The recipient (-s) deleted.',
  });
};

watch(
  () => inputValue.value,
  (newValue) => {
    tableRows.value = newValue.map((email) => ({ id: email, email }));
  },
  { immediate: true },
);
</script>

<template>
  <div class="flex gap-x-2">
    <Field v-model="email" :field="schema.email" />
    <BaseButton class="mt-1" variant="btn-secondary" @click="addEmail">
      Add</BaseButton
    >
  </div>
  <div v-if="inputValue.length" class="mt-4 bg-charcoal-0 px-4">
    <p
      class="mx-auto p-4 text-center text-button-1 uppercase tracking-widest text-charcoal-6"
    >
      Added recipients({{ inputValue.length }})
    </p>
    <div class="flex-center flex w-full gap-x-2">
      <SearchField
        v-model="search"
        class="w-full"
        placeholder="Search by Email"
      />
      <BaseButton
        icon="ic:baseline-delete"
        variant="btn-secondary"
        :disabled="selectedTableRows.length < 1"
        @click="removeEmail"
      />
    </div>

    <div class="max-h-[701px] overflow-y-auto">
      <DataTable
        :initialData="tableRows"
        :data="visibleTableRows"
        :settings="tableSettings"
        v-model:selectedTableRows="selectedTableRows"
      />
    </div>
  </div>
</template>
